import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ModalController} from '@ionic/angular';
import {
    BaseStepperFormComponent,
    filterList,
    LoadingService,
    Menu,
    MenuModifier,
    MenuModifierSelection,
    MenuModifierSelectionOption,
    MenuModifierSelectionService,
    MenuService,
    ModifierDisplayPosition,
    reorder,
    sortAlphabetical,
    sortBySortOrder,
    ToastService
} from 'brewbill-lib';

@Component({
    selector: 'bb-edit-menu-modifier-selection',
    templateUrl: './edit-menu-modifier-selection.component.html',
    styleUrls: ['./edit-menu-modifier-selection.component.scss'],
})
export class EditMenuModifierSelectionComponent extends BaseStepperFormComponent implements OnInit {
    @Input() modifierSelection: MenuModifierSelection;
    @Input() addMenuModifier: any;

    protected readonly sortAlphabetical = sortAlphabetical;

    selectedModifiers: MenuModifierSelectionOption[] = [];

    menu: Menu;
    formGroup: FormGroup = new FormGroup({});

    constructor(
        modalController: ModalController,
        private formBuilder: FormBuilder,
        private toastService: ToastService,
        private menuService: MenuService,
        private menuModifierSelectionService: MenuModifierSelectionService,
        private loadingService: LoadingService
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.menu = this.menuService.currentValue;
        this.modifierSelection = this.modifierSelection == null ? new MenuModifierSelection() : this.modifierSelection;

        this.selectedModifiers = this.modifierSelection.options == null ? [] : [...this.modifierSelection.options];

        this.formGroup = this.formBuilder.group(
            {
                id: this.modifierSelection.id,
                parentId: this.menu.id,
                name: [this.modifierSelection.name, Validators.required],
                description: this.modifierSelection.description,
                required: this.modifierSelection.required,
                minCount: this.modifierSelection.minCount,
                maxCount: this.modifierSelection.maxCount,
                groupByModifier: this.modifierSelection.groupByModifier
            }
        );

        this.formGroup.get('required').valueChanges.subscribe(v => {
            this.formGroup.get('minCount').setValidators(v ? [Validators.required] : null);

            if (v && !this.formGroup.get('minCount').value) {
                this.formGroup.get('minCount').setValue(1);
            } else {
                this.formGroup.get('minCount').setValue(null);
            }

            this.formGroup.get('minCount').updateValueAndValidity();
        });

        this.subscribe(this.menuService.current.subscribe(m => {
            this.menu = !!m ? new Menu(m) : null;
        }));
    }

    removeModifier(event: any, item: MenuModifierSelectionOption) {
        if (!!event) {
            event.stopPropagation();
        }
        this.selectedModifiers = this.selectedModifiers.filter(t => t.menuModifier.id !== item.menuModifier.id);
    }

    markModifiers(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.menuModifier.id === i.id);
        });
    }

    updateSelectedModifiers(items: any[]) {
        const update = this.selectedModifiers.filter(t =>
            items.some(i => i.id === t.menuModifier.id));

        items.filter(i => !update.some(t => t.menuModifier.id === i.id)).forEach(m => {
            update.push(new MenuModifierSelectionOption({
                menuModifier: m as MenuModifier,
                sortOrder: update.length,
                defaultOption: false
            }));
        });

        this.selectedModifiers = sortBySortOrder(update);
    }

    valid() {
        return this.formGroup.valid && !!this.selectedModifiers && this.selectedModifiers.length > 0;
    }

    updateSortOrder(items: any[]) {
        this.selectedModifiers = items;
    }

    async openAddMenuModifier(m: MenuModifierSelectionOption = null) {
        const modifier = await this.addMenuModifier(!!m ? m.menuModifier : null, true);
        if (modifier != null) {
            if (!m) {
                const modifierSelectionOption = new MenuModifierSelectionOption();
                modifierSelectionOption.menuModifier = modifier;
                modifierSelectionOption.sortOrder = this.selectedModifiers.length + 1;
                modifierSelectionOption.modifierDisplayPosition = ModifierDisplayPosition.STANDARD;
                this.selectedModifiers = [...this.selectedModifiers, modifierSelectionOption];
            } else {
                m.menuModifier = modifier;
            }
        }
    }

    onSubmit() {
        this.loadingService.present();
        const menuModifierSelection: MenuModifierSelection = new MenuModifierSelection(this.formGroup.value);
        menuModifierSelection.options = [...this.selectedModifiers];

        if (menuModifierSelection.id != null) {
            this.menuModifierSelectionService.update(menuModifierSelection).subscribe(
                async (t: MenuModifierSelection) => {
                    this.loadingService.dismiss();
                    await this.close(t);
                }, error => {
                    if (error.status === 422) {
                        this.toastService
                            .error('The modifier selection is out of sync from a previous change. Please refresh to make changes.');
                    }
                    this.close();
                }
            );
        } else {
            this.menuModifierSelectionService.create(menuModifierSelection).subscribe(
                async (t: MenuModifierSelection) => {
                    this.loadingService.dismiss();
                    await this.close(t);
                }
            );
        }
    }

    filterByName(items: any[], filter: string) {
        return filterList(items, filter, 'name');
    }

    protected readonly reorder = reorder;
}
